import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AppBootstrapLoader from "../components/AppBootstrapLoader";
import PrivateLayout from "../layouts/PrivateLayout";
import PublicLayout from "../layouts/PublicLayout";
import DashBoard from "../pages/ControlPanel/Dashboard/DashBoard";
import Devices from "../pages/ControlPanel/Devices/Devices";
import EventLog from "../pages/ControlPanel/EventLog/EventLog";
import LeaveAndHoliday from "../pages/ControlPanel/LeaveAndHoliday/LeaveAndHoliday";
import Reports from "../pages/ControlPanel/Reports/Reports";
import Settings from "../pages/ControlPanel/Settings/Settings";
import ShiftAndRoster from "../pages/ControlPanel/ShiftAndRoster/ShiftAndRoster";
import UserGroups from "../pages/ControlPanel/UserGroups/UserGroups";
import UserManagement from "../pages/ControlPanel/UserManagement/UserManagement";
import ForgotPassword from "../pages/ForgotPassword/ForgotPassword";
import CheckInbox from "../pages/Info/CheckInbox";
import Invitation from "../pages/Invitation/Invitation";
import Login from "../pages/Login/Login";
import SignupOrganization from "../pages/Signup/SignupOrganization";
import SignupOrganizationAuthentication from "../pages/Signup/SignupOrganizationAuthentication";
import Signup from "./../pages/Signup/Signup";
import { AUTH_ROUTES } from "../axios/axiosConfig";
import { LoadingProvider } from "../context/LoadingContext";
import VisitorManagement from "../pages/ControlPanel/VisitorManagement/VisitorManagement";
import NotFoundPage from "../pages/ControlPanel/NotFound/NotFoundPage";
import CompanyDetailsDialogForm from "../pages/ControlPanel/Navbar/CompanyDetailsDialogForm";
import MyAccountDialogForm from "../pages/ControlPanel/Navbar/MyAccountDialogForm";

// const PROTECT_ROUTES = [AUTH_ROUTES.signupOrganization, AUTH_ROUTES.signupOrganizationSSO];
const PROTECT_ROUTES = [];
const AUTH_ENTRY_ROUTES = ["/", "/login", "/sign-up", "/invitation", "/reset"];

const PageRouters = () => {
  const {
    isAuthenticated,
    isReady,
    isNormalLogin,
    profile,
    isAdmin,
    accessToken,
    infoUserSignUp,
  } = useAuth();
  const params = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = params[0].get("inviteCode");
  const activateCode = params[0].get("activateCode");
  const { pathname } = useLocation();
  const companyId = profile.companyId;

  const setCookie = (name, value, options = {}) => {
    if (options.expires) {
      options.expires = options.expires.toUTCString();
    }
    let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(
      value
    )}`;
    for (const [key, val] of Object.entries(options)) {
      updatedCookie += `; ${key}`;
      if (val !== true) {
        updatedCookie += `=${val}`;
      }
    }
    document.cookie = updatedCookie;
  };

  const LAST_ROUTE_KEY = "lastVisitedRoute";

  useEffect(() => {
    if (!isAuthenticated && PROTECT_ROUTES.some((r) => pathname.includes(r))) {
      return navigate(AUTH_ROUTES.login);
    }

    // Có invite/activate → giữ trang invitation, không đẩy dashboard
    if (inviteCode || activateCode || !isAuthenticated) return;

    if (companyId) {
      if (
        pathname === "/" ||
        pathname === "/login" ||
        pathname === "/sign-up" ||
        pathname === "/invitation"
      ) {
        // 1. redirectAfterLogin nếu bạn vẫn dùng chỗ khác
        const redirectUrl = localStorage.getItem("redirectAfterLogin");
        if (redirectUrl) {
          localStorage.removeItem("redirectAfterLogin");
          return navigate(redirectUrl, { replace: true });
        }

        // 2. lastVisitedRoute
        const lastRoute = localStorage.getItem(LAST_ROUTE_KEY);

        if (lastRoute) {
          const AUTH_ONLY_ROUTES = [
            "/login",
            "/sign-up",
            "/invitation",
            "/reset",
          ];

          const isAuthRoute =
            lastRoute === "/" || AUTH_ONLY_ROUTES.includes(lastRoute);

          if (!isAuthRoute) {
            if (isAdmin) {
              // admin đi thẳng vào lastRoute
              return navigate(lastRoute, { replace: true });
            } else {
              const allowedUserRoutes = [
                "/controlpanel/event-log-management",
                "/controlpanel/leave-and-holiday-management",
                "/controlpanel/reports",
              ];

              const isAllowed = allowedUserRoutes.some((r) =>
                lastRoute.startsWith(r)
              );

              return navigate(
                isAllowed
                  ? lastRoute
                  : "/controlpanel/event-log-management",
                { replace: true }
              );
            }
          }
        }

        // 3. Không có lastRoute hợp lệ → fallback như cũ
        return navigate(
          isAdmin
            ? "/controlpanel/dashboard"
            : "/controlpanel/event-log-management",
          { replace: true }
        );
      }

      // Non-admin: cho phép một số route, còn lại về event-log
      // Không navigate lại cùng pathname (sẽ mất query như requestShiftId/requestId)
      if (!isAdmin) {
        const allowedPaths = [
          "/controlpanel/leave-and-holiday-management",
          "/controlpanel/reports",
          "/controlpanel/shift-and-roster-management",
          "/controlpanel/user-management",
          "/controlpanel/event-log-management",
          "/controlpanel/my-profile",
          "/controlpanel/company-profile",
        ];
        const isAllowed = allowedPaths.some(
          (r) => pathname === r || pathname.startsWith(`${r}/`)
        );
        if (!isAllowed) {
          return navigate("/controlpanel/event-log-management");
        }
      }
    } else {
      if (!inviteCode && !activateCode && pathname !== "/invitation") {
        return navigate("/sign-up");
      }
    }
  }, [
    isAuthenticated,
    isNormalLogin,
    companyId,
    inviteCode,
    activateCode,
    infoUserSignUp,
    accessToken,
    pathname,
    isAdmin,
    navigate,
  ]);


  useEffect(() => {
    const isSignUpPage = pathname === "/sign-up";
    const isMissingInfo = !companyId && !accessToken && !infoUserSignUp?.email;

    if (isSignUpPage && isMissingInfo) {
      navigate("/login");
    }
  }, [pathname, companyId, accessToken, infoUserSignUp, navigate]);

  const isAuthEntryRoute = AUTH_ENTRY_ROUTES.includes(pathname);
  const isRedirectingAfterLogin =
    isReady &&
    isAuthenticated &&
    companyId &&
    isAuthEntryRoute &&
    !(pathname === "/invitation" && (inviteCode || activateCode));

  // Giữ loader khi auth chưa sẵn sàng — tránh flash trang login
  if (!isReady && isAuthEntryRoute) {
    return <AppBootstrapLoader />;
  }

  if (isRedirectingAfterLogin) {
    return <AppBootstrapLoader />;
  }

  // Activate: bỏ PublicLayout (navbar/Female giống login) — chỉ hiện Invitation loading → SSO
  if (pathname === "/invitation" && activateCode) {
    return (
      <LoadingProvider>
        <Invitation />
      </LoadingProvider>
    );
  }

  const defaultAuthenticatedRoute = isAdmin
    ? "/controlpanel/dashboard"
    : "/controlpanel/event-log-management";

  return (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated && companyId ? (
            <Navigate to={defaultAuthenticatedRoute} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route element={<LoadingProvider>
      <PublicLayout />
    </LoadingProvider>}>
        <Route path="login" element={<Login />} />
        <Route path="sign-up" element={<Signup />} />
        <Route path="reset" element={<CheckInbox />} />
        <Route path="invitation" element={<Invitation />} />
      </Route>

      <Route element={<PrivateLayout />}>
        <Route path="/controlpanel/dashboard" element={<DashBoard />} />
        <Route
          path="/controlpanel/user-management"
          element={<UserManagement />}
        />
        <Route
          path="/controlpanel/user-groups-management"
          element={<UserGroups />}
        />
        <Route
          path="/controlpanel/shift-and-roster-management"
          element={<ShiftAndRoster />}
        />
        <Route
          path="/controlpanel/leave-and-holiday-management"
          element={<LeaveAndHoliday />}
        />
        <Route path="/controlpanel/devices-management" element={<Devices />} />
        <Route
          path="/controlpanel/event-log-management"
          element={<EventLog />}
        />
        <Route path="/controlpanel/settings" element={<Settings />} />
        <Route
          path="/controlpanel/company-profile"
          element={<CompanyDetailsDialogForm />}
        />
        <Route
          path="/controlpanel/my-profile"
          element={<MyAccountDialogForm />}
        />
        <Route path="/controlpanel/visitor-management" element={<VisitorManagement />} />
        <Route path="/controlpanel/reports" element={<LoadingProvider>
          <Reports />
        </LoadingProvider>} />
        
      </Route>

      <Route path="*" element={<NotFoundPage />} />

    </Routes>
  );
};

export default PageRouters;
